import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  CssBaseline,
  Alert,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import CreditsBanner from "../Components/CreditsNudge";
import RandomIPGeneratorContent from "../Contents/RandomIpAddressContent.js";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

const getLocalStorageValue = (key, defaultValue) => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(key);
    try {
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return saved !== null ? saved : defaultValue;
    }
  }
  return defaultValue;
};

function generateRandomIP() {
  return Array.from({ length: 4 }, () => Math.floor(Math.random() * 256)).join(
    "."
  );
}

export default function RandomIPGenerator({ user, login, isLoggedIn }) {
  const [error, setError] = useState("");
  const [count, setCount] = useState(() => getLocalStorageValue("ipCount", 10));
  const [separator, setSeparator] = useState(() =>
    getLocalStorageValue("ipSeparator", "line")
  );
  const [ips, setIps] = useState([]);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    try {
      localStorage.setItem("ipCount", JSON.stringify(count));
      localStorage.setItem("ipSeparator", JSON.stringify(separator));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  }, [count, separator]);

const handleGenerate = async () => {
  setError("");
  setSuccess("");
  setLoading(true);

  try {
    // Determine tool key from path for guest usage tracking
    const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
    const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";
    const guestUsedKey = `${toolKey}_guestUsed`;

    // Guest one-time access check
    if (!isLoggedIn) {
      const hasUsedGuestAccess = localStorage.getItem(guestUsedKey);
      if (hasUsedGuestAccess) {
        setError(message.loginPrompt);
        setTimeout(() => {
          login();
        }, 1500);
        setLoading(false);
        return;
      } else {
        localStorage.setItem(guestUsedKey, "true");
      }
    }

    // Logged in user checks
    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      if (count > 100) {
        setError(
          "You can only generate up to 100 IP addresses on the Free plan. Please upgrade to the Paid plan for more."
        );
        setLoading(false);
        return;
      }
      if (credits.remaining < 1) {
        setError(
          "Insufficient credits to generate IP addresses. Please upgrade your plan."
        );
        setLoading(false);
        return;
      }
      const creditsResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
        { toolName: "random_ip_generator" }
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } else if (isLoggedIn && user.account_type.toLowerCase() === "paid") {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
        { toolName: "random_ip_generator" }
      );
    }

    // Generate IPs
    const generated = Array.from({ length: count }, () => generateRandomIP());
    setIps(generated);
    setSuccess("IP addresses generated successfully!");
  } catch (err) {
    console.error(err);
    setError("An error occurred while generating IP addresses.");
  } finally {
    setLoading(false);
  }
};


  const getSeparatorSymbol = () => {
    switch (separator) {
      case "comma":
        return ",";
      case "space":
        return " ";
      case "commaSpace":
        return ", ";
      default:
        return "\n";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess("IP addresses copied to clipboard!");
  };

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);

    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      setError("Download is only available for Paid users.");
    }
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = (format) => {
    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      setError("Download is only available for Paid users.");
      return;
    }

    let content = "";
    let mimeType = "text/plain";
    const fileName = `ips.${format}`;

    switch (format) {
      case "txt":
        content = ips.join(getSeparatorSymbol());
        mimeType = "text/plain";
        break;
      case "csv":
        content = ips.join(",");
        mimeType = "text/csv";
        break;
      case "json":
        content = JSON.stringify(ips, null, 2);
        mimeType = "application/json";
        break;
      default:
        content = ips.join(getSeparatorSymbol());
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setAnchorEl(null);
    setSuccess(`Downloaded as ${format.toUpperCase()}!`);
  };

  const separatorSymbol = getSeparatorSymbol();

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Random IP Generator 🌐
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Generate random IPv4 addresses for testing or placeholder use
          </SubtitleText>

          <Box mb={3}>
            <Box
              display="flex"
              flexWrap="wrap"
              gap={2}
              alignItems="center"
              justifyContent="center"
              sx={{ "& > *": { my: 0 }, gap: 5 }}
            >
              <TextField
                label="How many"
                type="number"
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: 180 }}
              />

              <StyledButton
                variant="contained"
                color="success"
                onClick={handleGenerate}
                loading={loading}
                sx={{
                  whiteSpace: "nowrap",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  marginTop: 0,
                  transform: "translateY(-4px)",
                }}
              >
                Generate
              </StyledButton>
            </Box>
            {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>⚠️ Free Plan Limitations:</b> Download is only available for
                Paid plan users and can generate upto 100 IPs. For Free plan
                users, 1 credit will be deducted for each generation.{" "}
                <b>All Paid plan users</b> enjoy this tool for free 🎁{" "}
                <b>without any credit deduction</b>.
              </Typography>
            ) : (
              isLoggedIn && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  mt={2}
                >
                  <b>
                    🎁 Good news! For all Paid plan users, IP generation and
                    downloads are completely complimentary — no credits will be
                    deducted.
                  </b>
                </Typography>
              )
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 4 }}>
              {ips.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => copyToClipboard(ips.join(separatorSymbol))}
                    aria-label="Copy IPs"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleDownloadClick}
                    aria-label="Download IPs"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleDownloadClose}
                  >
                    <MenuItem onClick={() => handleDownload("txt")}>
                      TXT
                    </MenuItem>
                    <MenuItem onClick={() => handleDownload("csv")}>
                      CSV
                    </MenuItem>
                    <MenuItem onClick={() => handleDownload("json")}>
                      JSON
                    </MenuItem>
                  </Menu>
                </Box>
              )}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  minHeight: 150,
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {ips.join(separatorSymbol) ||
                  "Generated IP addresses will appear here."}
              </Box>
            </Box>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b>💡 Note:</b> These IP addresses are randomly generated and do
              not represent real, assigned devices.
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="generate-random-ip-address"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <RandomIPGeneratorContent />
      </PageContainer>
    </>
  );
}
